import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import {
  TripDealsResponseSchema,
  dealUndoPatch,
  dealsSummary,
  formatMoney,
  inBasket,
  planTripDeals,
  revertPatch,
  tripItemDealState,
  type CatalogItem,
  type DealOption,
  type DealProposal,
  type FxRates,
  type Store,
  type SyncChangeData,
  type TripDealsResponse,
  type TripItem,
  type TripWithItems,
} from '@sp/shared'
import { api } from '../lib/api.ts'
import { HttpError } from '../lib/http.ts'
import { readJson, writeJson } from '../lib/storage.ts'
import { errorMessage, useResource } from '../lib/useResource.ts'
import { formatCzkRounded, formatQty, formatShortWeekday, formatTripDate, intoStore } from '../lib/format.ts'
import { chainLabel, offerPack, offerTitle, offerValidity } from '../lib/offers.ts'
import { sectionsFor } from '../pages/trip/shared.ts'
import type { TripSync } from '../lib/tripSync.ts'
import { Sheet } from './Sheet.tsx'
import { OfferImage } from './OfferImage.tsx'
import { Icon } from './Icon.tsx'
import { DealTag, tripDealLabel, type DealLabel } from './OfferBadge.tsx'
import { Banner, Button, Checkbox, IconButton, Label, Paragraph, SectionHead, Spinner, TextCol, TextField } from './ui.tsx'

/** Jak dlouho zmenene radky po pouziti slev blikaji (animace deal-flash v app.css). */
const FLASH_MS = 2400
/** Slevy se hledaji znovu az chvili po posledni zmene seznamu, ne po kazdem tuknuti. */
const REFRESH_MS = 900

/** Po pouziti: co vratit a co rict v pruhu nad seznamem. */
interface Applied {
  count: number
  savingCzkMinor: number
  reverts: SyncChangeData[]
}

function slevy(n: number): string {
  if (n === 1) return '1 slevu'
  if (n >= 2 && n <= 4) return `${n} slevy`
  return `${n} slev`
}

function polozkyMaji(n: number): string {
  if (n === 1) return '1 položka má'
  if (n >= 2 && n <= 4) return `${n} položky mají`
  return `${n} položek má`
}

/** „1 položka je v akci", „3 položky jsou teď v akci", „5 položek je v akci". */
export function inDealText(n: number, now = false): string {
  const tail = now ? 'teď v akci' : 'v akci'
  if (n === 1) return `1 položka je ${tail}`
  if (n >= 2 && n <= 4) return `${n} položky jsou ${tail}`
  return `${n} položek je ${tail}`
}

function useDebounced<T>(value: T, ms: number): T {
  const [current, setCurrent] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setCurrent(value), ms)
    return () => clearTimeout(timer)
  }, [value, ms])
  return current
}

function parseDeals(data: unknown): TripDealsResponse | null {
  const parsed = TripDealsResponseSchema.safeParse(data)
  return parsed.success ? parsed.data : null
}

/**
 * Slevy pro polozky nakupu (navrh v3). Hledaji se samy po otevreni a po zmene
 * seznamu; nad seznamem je pruh „N položek je v akci", detail polozky ma sekci
 * Slevy. Nic se neprepise bez kontroly (Najit slevy, 2026-09-15).
 */
export function useTripDeals({
  trip,
  stores,
  sync,
  rates,
  catalog = [],
  onCatalogSaved,
}: {
  trip: TripWithItems
  stores: readonly Store[]
  sync: TripSync
  rates: FxRates
  /** Zbozi skupiny — podle jeho vyrazu se akce hledaji a v detailu jdou upravit. */
  catalog?: readonly CatalogItem[] | undefined
  onCatalogSaved?: ((item: CatalogItem) => void) | undefined
}) {
  const [open, setOpen] = useState(false)
  const [applied, setApplied] = useState<Applied | null>(null)
  // Radky, kterym sleva prave zmenila cenu nebo obchod — kratce se zvyrazni, at je videt, co se stalo.
  const [flashIds, setFlashIds] = useState<ReadonlySet<string>>(new Set())
  const hiddenKey = `sp:slevy-skryto:${trip.id}`
  const [hidden, setHidden] = useState<string | null>(() => {
    const value = readJson(hiddenKey)
    return typeof value === 'string' ? value : null
  })

  const enabled = trip.status !== 'done'
  const openItems = trip.items
    .filter((i) => !inBasket(i))
    .map((i) => `${i.id}:${i.storeId ?? ''}:${i.catalogItemId ?? i.name}`)
    .sort()
    .join('|')
  const signature = useDebounced([trip.plannedDate ?? '', trip.storeIds.join(','), openItems].join('#'), REFRESH_MS)
  const wanted = enabled && openItems !== ''
  const deals = useResource<TripDealsResponse | null>(
    () => (wanted ? api.trips.deals(trip.id) : Promise.resolve(null)),
    [trip.id, wanted, signature],
    { key: `sp:slevy:${trip.id}`, parse: parseDeals },
  )

  useEffect(() => {
    if (flashIds.size === 0) return
    const timer = setTimeout(() => setFlashIds(new Set()), FLASH_MS)
    return () => clearTimeout(timer)
  }, [flashIds])

  const data = deals.res.state === 'ok' ? deals.res.data : null
  const allProposals = data && enabled ? planTripDeals(trip.items, data.items, rates) : []
  // Detail polozky ukaze i jine akce k polozce s pouzitou akci; pruh a kontrola ji uz nepocitaji.
  const byItem = new Map(allProposals.map((p) => [p.item.id, p]))
  const proposals = allProposals.filter((p) => !p.item.deal)
  const summary = dealsSummary(proposals)
  // Skryty pruh se vrati, az pribude jina akce nebo polozka.
  const proposalsKey = proposals
    .map((p) => `${p.item.id}:${p.options.map((o) => o.offer.id).join(',')}`)
    .sort()
    .join('|')
  const storeName = (id: string | null) => (id ? (stores.find((s) => s.id === id)?.name ?? null) : null)

  function hide() {
    setHidden(proposalsKey)
    writeJson(hiddenKey, proposalsKey)
  }

  function enqueue(pairs: { item: TripItem; option: DealOption }[]) {
    const changedAt = new Date().toISOString()
    const changes: SyncChangeData[] = []
    const reverts: SyncChangeData[] = []
    let savingCzkMinor = 0
    for (const { item, option } of pairs) {
      changes.push({ op: 'patch', itemId: item.id, patch: option.patch, changedAt })
      reverts.push({ op: 'patch', itemId: item.id, patch: revertPatch(item, option.patch), changedAt })
      savingCzkMinor += Math.max(0, option.savingCzkMinor ?? 0)
    }
    if (changes.length === 0) return
    sync.enqueue(changes)
    setApplied({ count: changes.length, savingCzkMinor, reverts })
    setFlashIds(new Set(pairs.map((pair) => pair.item.id)))
  }

  function applyReviewed(chosen: ReadonlyMap<string, number | null>) {
    setOpen(false)
    const pairs = proposals.flatMap((proposal) => {
      const option = pickedOption(proposal, chosen)
      return option ? [{ item: proposal.item, option }] : []
    })
    if (pairs.length === 0) hide()
    enqueue(pairs)
  }

  function undo() {
    if (!applied) return
    // Novy cas zmeny — vraceni musi prebit prave odeslane slevy.
    const changedAt = new Date().toISOString()
    sync.enqueue(applied.reverts.map((change) => ({ ...change, changedAt })))
    setApplied(null)
    setFlashIds(new Set(applied.reverts.flatMap((change) => (change.op === 'patch' ? [change.itemId] : []))))
  }

  // Mistni datum (sv-SE je tvar 2026-09-19), ne UTC.
  const today = new Date().toLocaleDateString('sv-SE')
  const banner = applied ? (
    <Banner
      tone="info"
      className="banner-deal"
      action={
        <>
          <Button kind="link" icon="refresh" compact onClick={undo}>
            Vrátit
          </Button>
          <IconButton icon="x" label="Skrýt" tone="muted" onClick={() => setApplied(null)} />
        </>
      }
    >
      <TextCol
        title={`${polozkyMaji(applied.count)} cenu z akce`}
        sub={applied.savingCzkMinor > 0 ? `odhad je o ${formatCzkRounded(applied.savingCzkMinor)} nižší` : undefined}
      />
    </Banner>
  ) : summary.count > 0 && hidden !== proposalsKey && data ? (
    <Banner
      tone="info"
      className="banner-deal deals-strip"
      action={
        <>
          <Button compact onClick={() => setOpen(true)}>
            Projít
          </Button>
          <IconButton icon="x" label="Skrýt" tone="muted" onClick={hide} />
        </>
      }
    >
      <span className="deals-strip-icon" aria-hidden="true">
        %
      </span>
      <TextCol
        title={inDealText(summary.count)}
        sub={[
          summary.savingCzkMinor > 0 ? `ušetříš ≈ ${formatCzkRounded(summary.savingCzkMinor)}` : null,
          data.date <= today ? 'platí dnes' : `platí ${formatShortWeekday(data.date)}`,
        ]
          .filter(Boolean)
          .join(' · ')}
      />
    </Banner>
  ) : null

  const sheet = open && data && (
    <DealsSheet
      trip={trip}
      stores={stores}
      data={data}
      proposals={proposals}
      onClose={() => setOpen(false)}
      onApply={applyReviewed}
    />
  )

  /** Trida pro radek polozky, ktery se ma po pouziti (nebo vraceni) slev zvyraznit. */
  const flashClass = (itemId: string) => (flashIds.has(itemId) ? 'deal-flash' : undefined)

  /** Stitek polozky; null = nic k ukazani. */
  function dealLabel(item: TripItem): DealLabel | null {
    const state = tripItemDealState(item, byItem.get(item.id), trip.plannedDate)
    return state ? tripDealLabel(state, storeName) : null
  }

  function itemDeals(item: TripItem): ReactNode {
    if (inBasket(item) && !item.deal) return null
    const catalogItem = item.catalogItemId ? (catalog.find((c) => c.id === item.catalogItemId) ?? null) : null
    return (
      <ItemDeals
        key={item.id}
        item={item}
        proposal={byItem.get(item.id)}
        state={
          !enabled || inBasket(item)
            ? 'off'
            : deals.res.state === 'loading'
              ? 'loading'
              : deals.res.state === 'error' && !data
                ? deals.offline
                  ? 'offline'
                  : 'error'
                : 'ok'
        }
        storeName={storeName}
        plannedDate={trip.plannedDate}
        catalogItem={catalogItem}
        onApply={(option) => enqueue([{ item, option }])}
        onUndoDeal={() => sync.change(item.id, dealUndoPatch(item))}
        onTermsSaved={(updated) => {
          onCatalogSaved?.(updated)
          deals.reload()
        }}
      />
    )
  }

  return {
    dealsSheet: sheet,
    dealsBanner: banner,
    flashClass,
    dealLabel,
    itemDeals,
  }
}

function pickedOption(proposal: DealProposal, chosen: ReadonlyMap<string, number | null>): DealOption | null {
  const index = chosen.has(proposal.item.id) ? chosen.get(proposal.item.id) : proposal.preselected
  return index === null || index === undefined ? null : (proposal.options[index] ?? null)
}

function DealsSheet({
  trip,
  stores,
  data,
  proposals,
  onClose,
  onApply,
}: {
  trip: TripWithItems
  stores: readonly Store[]
  data: TripDealsResponse
  proposals: DealProposal[]
  onClose(): void
  onApply(chosen: ReadonlyMap<string, number | null>): void
}) {
  // Jen zmeny oproti predvyberu; polozka bez zaznamu ma predvybranou akci.
  const [chosen, setChosen] = useState<ReadonlyMap<string, number | null>>(new Map())

  const route = trip.storeIds.flatMap((id) => stores.find((s) => s.id === id) ?? [])
  const subtitle = [formatTripDate(data.date), route.length > 0 ? route.map((s) => s.name).join(', ') : 'kdekoli'].join(
    ' · ',
  )
  const picked = proposals.flatMap((p) => pickedOption(p, chosen) ?? [])
  const saving = picked.reduce((sum, option) => sum + Math.max(0, option.savingCzkMinor ?? 0), 0)
  const openCount = trip.items.filter((i) => !inBasket(i)).length

  const select = (itemId: string, index: number | null) => setChosen((prev) => new Map(prev).set(itemId, index))

  const footer = proposals.length > 0 && (
    <div className="deals-footer">
      <span className="meta muted tabular">
        {`Akce pro ${proposals.length} z ${openCount} položek`}
        {saving > 0 && <span className="deal-saving">{` · ušetříš ≈ ${formatCzkRounded(saving)}`}</span>}
      </span>
      <Button kind="primary" full onClick={() => onApply(chosen)}>
        {picked.length > 0 ? `Použít ${slevy(picked.length)}` : 'Nic nepoužít'}
      </Button>
    </div>
  )

  return (
    <Sheet open onClose={onClose} title={inDealText(proposals.length)} subtitle={subtitle} footer={footer} className="deals-sheet">
      <DealsBody data={data} trip={trip} stores={stores} proposals={proposals} chosen={chosen} onSelect={select} />
    </Sheet>
  )
}

function DealsBody({
  data,
  trip,
  stores,
  proposals,
  chosen,
  onSelect,
}: {
  data: TripDealsResponse
  trip: TripWithItems
  stores: readonly Store[]
  proposals: DealProposal[]
  chosen: ReadonlyMap<string, number | null>
  onSelect(itemId: string, index: number | null): void
}) {
  const knownUntil = data.knownUntil ? new Date(data.knownUntil) : null
  const beyondLeaflets = knownUntil !== null && knownUntil.getTime() < Date.parse(`${data.date}T00:00:00`)
  const withoutOffers = data.storesWithoutOffers.flatMap((id) => stores.find((s) => s.id === id)?.name ?? [])

  const byItem = new Map(proposals.map((p) => [p.item.id, p]))
  const storeName = (id: string | null) => stores.find((s) => s.id === id)?.name ?? null
  return (
    <div className="stack-8">
      {beyondLeaflets && (
        <Banner tone="warning">
          {`Na ${formatTripDate(data.date)} ještě týdenní letáky nevyšly – ukazuji jen akce, o kterých už víme.`}
        </Banner>
      )}
      {withoutOffers.length > 0 && <Paragraph size="meta">{`Bez letáku: ${withoutOffers.join(', ')}`}</Paragraph>}
      {sectionsFor(trip, stores).map((section) => {
        const inSection = section.items.flatMap((i) => byItem.get(i.id) ?? [])
        if (inSection.length === 0) return null
        return (
          <div key={section.key} className="stack-8">
            <SectionHead title={section.title} />
            <div className="deal-grid">
              {inSection.map((proposal) => (
                <DealCard
                  key={proposal.item.id}
                  proposal={proposal}
                  picked={chosen.has(proposal.item.id) ? (chosen.get(proposal.item.id) ?? null) : proposal.preselected}
                  storeName={storeName}
                  onSelect={(index) => onSelect(proposal.item.id, index)}
                />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export interface StoreOptions {
  key: string
  /** Nazev obchodu na trase, u nakupu „kdekoli" retezec. */
  title: string
  /** Pouziti polozku presune do tohoto obchodu. */
  moves: boolean
  entries: { option: DealOption; index: number }[]
}

/**
 * Akce jedne polozky po obchodech (Richard 2026-09-16: vsechno v jednom
 * seznamu se necetlo). Obchod polozky prvni, ostatni v poradi, v jakem prisly
 * (planTripDeals je radi od nejlevnejsi).
 */
export function optionsByStore(options: readonly DealOption[], storeName: (id: string | null) => string | null): StoreOptions[] {
  const groups = new Map<string, StoreOptions>()
  options.forEach((option, index) => {
    const key = option.storeId ?? `retezec:${option.offer.chain}`
    const group = groups.get(key) ?? {
      key,
      title: storeName(option.storeId) ?? chainLabel(option.offer.chain),
      moves: option.moves,
      entries: [],
    }
    group.entries.push({ option, index })
    groups.set(key, group)
  })
  return [...groups.values()].sort((a, b) => Number(a.moves) - Number(b.moves))
}

function StoreHead({ group, own }: { group: StoreOptions; own: boolean }) {
  return (
    <div className="deal-store-head">
      <span className="deal-store-name">{group.title}</span>
      {own && <span className="offer-tag is-best">obchod položky</span>}
      {group.moves && <span className="offer-tag">přesune tam</span>}
    </div>
  )
}

function whereText(item: TripItem, option: DealOption, storeName: (id: string | null) => string | null): string | null {
  if (!option.moves) return null
  const name = storeName(option.storeId)
  if (!name) return null
  return item.storeId ? `přesune ${intoStore(name)}` : `přidá ${intoStore(name)}`
}

function optionDetails(
  item: TripItem,
  option: DealOption,
  storeName: (id: string | null) => string | null,
  withWhere = true,
): string {
  return [
    withWhere ? whereText(item, option, storeName) : null,
    offerPack(option.offer),
    offerValidity(option.offer),
    option.offer.requiresCard ? 'jen s kartou' : null,
  ]
    .filter(Boolean)
    .join(' · ')
}

function DealCard({
  proposal,
  picked,
  storeName,
  onSelect,
}: {
  proposal: DealProposal
  picked: number | null
  storeName(id: string | null): string | null
  onSelect(index: number | null): void
}) {
  const { item, options, by } = proposal
  const single = options.length === 1 ? options[0] : undefined
  const current =
    item.expectedPriceMinor !== null && item.expectedCurrency
      ? `teď ${formatMoney(item.expectedPriceMinor, item.expectedCurrency)}`
      : 'zatím bez ceny'
  const sub = [formatQty(item.qty, item.unit), current, single ? null : `${options.length} akce`].filter(Boolean).join(' · ')

  return (
    <div className={`deal-card${picked !== null ? ' is-selected' : ''}`}>
      <button
        type="button"
        className="deal-card-head"
        aria-pressed={picked !== null}
        onClick={() => onSelect(picked !== null ? null : (proposal.preselected ?? 0))}
      >
        <Checkbox checked={picked !== null} />
        <TextCol
          title={item.name}
          sub={
            by === 'name' ? (
              <>
                {sub} · <span className="deal-by-name">podle názvu</span>
              </>
            ) : (
              sub
            )
          }
        />
        {single && <DealPrice option={single} />}
      </button>
      {single ? (
        <div className="deal-offer">
          <OfferImage offer={single.offer} size="thumb" />
          <TextCol title={offerTitle(single.offer)} sub={optionDetails(item, single, storeName)} />
        </div>
      ) : (
        <div className="deal-options" role="radiogroup" aria-label={`Akce pro ${item.name}`}>
          {optionsByStore(options, storeName).map((group) => (
            <div key={group.key} className="deal-store" role="group" aria-label={group.title}>
              <StoreHead group={group} own={!group.moves && item.storeId !== null} />
              {group.entries.map(({ option, index }) => (
                <button
                  key={option.offer.id}
                  type="button"
                  role="radio"
                  aria-checked={picked === index}
                  className={`deal-option${picked === index ? ' is-active' : ''}`}
                  onClick={() => onSelect(index)}
                >
                  <span className="deal-radio" aria-hidden="true" />
                  <OfferImage offer={option.offer} size="thumb" />
                  <TextCol title={offerTitle(option.offer)} sub={optionDetails(item, option, storeName, false)} />
                  <DealPrice option={option} />
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
      {by === 'name' && (
        <Paragraph size="meta" className="deal-hint">
          Položka není ze šablony, shoda je jen podle názvu – zkontroluj, že je to ono.
        </Paragraph>
      )}
    </div>
  )
}

function DealPrice({ option }: { option: DealOption }) {
  const { offer, savingCzkMinor } = option
  const foreign = offer.currency !== 'CZK'
  // Akce bez ceny za kus (druhy za 1 zl) — co plati, rika popis pod nazvem.
  if (offer.priceMinor === null) {
    return (
      <span className="deal-price">
        <strong>v letáku</strong>
      </span>
    )
  }
  const priceMinor = offer.priceMinor
  return (
    <span className="deal-price">
      <strong>{formatMoney(priceMinor, offer.currency)}</strong>
      {savingCzkMinor !== null && savingCzkMinor > 0 && (
        <span className="deal-saving">{`${foreign ? '≈ ' : ''}−${formatCzkRounded(savingCzkMinor)}`}</span>
      )}
      {savingCzkMinor !== null && savingCzkMinor < 0 && <span className="deal-worse">dražší</span>}
      {savingCzkMinor === null && offer.oldPriceMinor !== null && offer.oldPriceMinor > priceMinor && (
        <s className="muted">{formatMoney(offer.oldPriceMinor, offer.currency)}</s>
      )}
    </span>
  )
}

/**
 * Sekce Slevy v detailu polozky nakupu — nahradila „Najít slevu" a „Použít cenu".
 * Stejna data jako pruh nad seznamem, akce se pouzije jednim tuknutim (i s presunem).
 */
function ItemDeals({
  item,
  proposal,
  state,
  storeName,
  plannedDate,
  catalogItem,
  onApply,
  onUndoDeal,
  onTermsSaved,
}: {
  item: TripItem
  proposal: DealProposal | undefined
  state: 'ok' | 'loading' | 'offline' | 'error' | 'off'
  storeName(id: string | null): string | null
  plannedDate: string | null
  catalogItem: CatalogItem | null
  onApply(option: DealOption): void
  onUndoDeal(): void
  onTermsSaved(item: CatalogItem): void
}) {
  const [editing, setEditing] = useState(false)
  const [terms, setTerms] = useState(catalogItem?.offerTerms ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const applied = item.deal ? tripItemDealState(item, undefined, plannedDate) : null
  const options = proposal?.options ?? []
  const own = storeName(item.storeId)

  async function saveTerms(e: FormEvent) {
    e.preventDefault()
    if (!catalogItem) return
    setBusy(true)
    setError(null)
    try {
      const updated = await api.catalog.update(catalogItem.id, { offerTerms: terms.trim() || null })
      setEditing(false)
      onTermsSaved(updated)
    } catch (err) {
      setError(err instanceof HttpError && err.isOffline ? 'Hledání jde změnit jen se signálem.' : errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="stack-8 item-offers" aria-label="Slevy">
      <Label as="span">Slevy</Label>
      {applied && item.deal && (
        <div className="offer-inline">
          <DealTag label={tripDealLabel(applied, storeName)} />
          {/* Nazev z letaku byva uz v poznamce nad tim — dvakrat ho neukazovat. */}
          <span className="offer-inline-text">{item.note === item.deal.name ? null : item.deal.name}</span>
          <Button compact onClick={onUndoDeal}>
            Zrušit akci
          </Button>
        </div>
      )}
      {state === 'loading' && options.length === 0 && <Spinner label="Hledám akce v letácích…" />}
      {state === 'offline' && options.length === 0 && (
        <Paragraph size="meta">Slevy se načtou, až bude signál.</Paragraph>
      )}
      {state === 'error' && options.length === 0 && <Paragraph size="meta">Slevy se teď nepodařilo načíst.</Paragraph>}
      {state === 'ok' && !item.deal && options.every((option) => option.moves) && (
        <Paragraph size="meta">{own ? `${own}: teď tu v akci není` : 'Teď v akci není'}</Paragraph>
      )}
      {optionsByStore(options, storeName).map((group) => (
        <div key={group.key} className="stack-4">
          <StoreHead group={group} own={!group.moves && item.storeId !== null} />
          {group.entries.map(({ option }) => (
            // Cela karta je tlacitko: samostatne tlacitko pod kazdou akci trhalo seznam
            // (Richard 2026-09-16). Omyl vrati pruh „Vrátit".
            <button
              key={option.offer.id}
              type="button"
              className="offer-group-row item-deal"
              aria-label={
                option.moves && storeName(option.storeId)
                  ? `Přesunout ${intoStore(storeName(option.storeId) ?? '')} a použít cenu ${offerTitle(option.offer)}`
                  : `Použít cenu z akce ${offerTitle(option.offer)}`
              }
              onClick={() => onApply(option)}
            >
              <OfferImage offer={option.offer} size="thumb" />
              <TextCol
                title={offerTitle(option.offer)}
                sub={[offerPack(option.offer), offerValidity(option.offer), option.offer.requiresCard ? 'jen s kartou' : null]
                  .filter(Boolean)
                  .join(' · ')}
              />
              <span className="item-deal-side">
                <DealPrice option={option} />
                <span className="item-deal-use" aria-hidden="true">
                  <Icon name={option.moves ? 'arrow_right' : 'check'} size={14} strokeWidth={2.4} />
                  {option.moves ? 'Přesunout' : 'Použít'}
                </span>
              </span>
            </button>
          ))}
        </div>
      ))}
      {proposal?.by === 'name' && options.length > 0 && (
        <Paragraph size="meta">Shoda je jen podle názvu – zkontroluj, že je to ono.</Paragraph>
      )}
      {state !== 'off' && (
        <div className="item-offers-terms">
          <Paragraph size="meta">
            {catalogItem ? `Hledá se jako: ${catalogItem.offerTerms || catalogItem.name}` : `Hledá se podle názvu: ${item.name}`}
          </Paragraph>
          {catalogItem && !editing && (
            <Button kind="text" compact onClick={() => setEditing(true)}>
              Upravit
            </Button>
          )}
        </div>
      )}
      {editing && catalogItem && (
        <form className="stack-8 inline-editor-block" onSubmit={(e) => void saveTerms(e)}>
          <TextField
            label="Hledat v akcích jako"
            hint="Názvy z letáků oddělené čárkou, třeba Butter, Masło. Prázdné pole = podle názvu."
            value={terms}
            onChange={(e) => setTerms(e.target.value)}
            maxLength={300}
            autoFocus
            error={error}
          />
          <div className="button-row">
            <Button compact onClick={() => setEditing(false)}>
              Zrušit
            </Button>
            <Button compact kind="primary" type="submit" disabled={busy}>
              Uložit a hledat
            </Button>
          </div>
        </form>
      )}
    </section>
  )
}
